import { Directive, OnDestroy } from '@angular/core';
import { Exercicio, FuncaoService, GlobalService, Login, Relatorio } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BalancoService } from '../service/balanco.service';

@Directive()
export class Anexo12Balanco implements OnDestroy {
  protected funcaoService: FuncaoService;
  protected globalService: GlobalService;
  private login: Login = new Login();
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected balancoServico: BalancoService,
    protected exercicio: Exercicio) {
    this.funcaoService = new FuncaoService();
    this.globalService = new GlobalService();
    this.login = GlobalService.obterSessaoLogin();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public montarRelatorio(orgaos: number[]) {
    this.balancoServico.obterBalancoOrcamentario(this.exercicio.id, orgaos)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(dados => {
        Relatorio.imprimirPersonalizado(
          `ANEXO 12 - BALANÇO ORÇAMENTÁRIO`
          , this.login.usuario.nome,
          this.login.orgao.nome, this.login.brasao,
          this.conteudoReceita(dados[0], dados[1]).concat(this.conteudoDespesa(dados[1], dados[0])),
          'portrait', 'ANEXO 12 - BALANÇO ORÇAMENTÁRIO',
          {
            linhas: {
              hLineWidth() {
                return 1;
              },
              vLineWidth() {
                return 1;
              },
              hLineColor() {
                return 'black';
              },
              paddingLeft() {
                return 3;
              },
              paddingRight() {
                return 3;
              }
            }
          });
      });
  }

  private conteudoReceita(dados: any[], dadosDespesa: any[]): {}[] {

    // loop para pegar o total liquidado e verificar se houve defict ou superavit
    const gruposDespesa = this.funcaoService.agrupar(dadosDespesa, 'tipo',
      ['dotacao', 'adicionais', 'empenhado', 'liquidado', 'pago']);
    let totalLiquidado = 0;
    for (const grupo of gruposDespesa) {
      totalLiquidado = totalLiquidado + grupo.totalizadores['liquidado'];
    }
    // monta o cabecalho
    const registros: {}[] = [
      [{
        text: `EXERCÍCIO: ${this.exercicio.ano}`,
        alignment: 'center', bold: true, colSpan: 5, fontSize: 13
      }, '', '', '', ''],
      [{
        text: 'RECEITAS',
        alignment: 'center',
        bold: true, fontSize: 7
      }, {
        text: 'PREVISÃO INICIAL',
        alignment: 'center',
        bold: true, fontSize: 7
      }, {
        text: 'PREVISÃO ATUALIZADA (a)',
        alignment: 'center',
        bold: true, fontSize: 7
      }, {
        text: 'RECEITAS REALIZADAS (b)',
        alignment: 'center',
        bold: true, fontSize: 7
      }, {
        text: 'SALDO (b-a)',
        alignment: 'center',
        bold: true, fontSize: 7
      }]
    ];

    // monta o agrupamento do relatório
    const grupos = this.funcaoService.agrupar(dados, 'tipo',
      ['previsao', 'atualizado', 'previsto_total', 'arrecadado']);
    const totalizador = new Array<any>();

    for (const item of grupos) {
      if (item.grupo !== 'REFINANCIAMENTO ( II )') {
        registros.push([
          {
            text: item.grupo, bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              item.totalizadores[`previsao`]), alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              item.totalizadores[`atualizado`]), alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              item.totalizadores[`arrecadado`]), alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              +item.totalizadores[`atualizado`] - +item.totalizadores[`arrecadado`]), alignment: 'right', bold: true, fontSize: 7
          }
        ]);

        const titulos = this.funcaoService.agrupar(item.registros, 'titulo',
          ['previsao', 'atualizado', 'previsto_total', 'arrecadado']);

        for (const titulo of titulos) {
          registros.push([
            {
              text: titulo.grupo, bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(
                titulo.totalizadores[`previsao`]), alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(
                titulo.totalizadores[`atualizado`]), alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(
                titulo.totalizadores[`arrecadado`]), alignment: 'right', bold: true, fontSize: 7
            },
            {
              text: this.funcaoService.convertToBrNumber(
                +titulo.totalizadores[`atualizado`] - +titulo.totalizadores[`arrecadado`]), alignment: 'right', bold: true, fontSize: 7
            }
          ]);
          // lista os registros do relatorio
          for (const registro of titulo.registros) {
            if (registro.tamanho > 2) {
              registros.push([
                {
                  text: `\t\t${registro.nome}`, fontSize: 7
                },
                { text: this.funcaoService.convertToBrNumber(registro.previsao), alignment: 'right', fontSize: 7 },
                { text: this.funcaoService.convertToBrNumber(registro.atualizado), alignment: 'right', fontSize: 7 },
                { text: this.funcaoService.convertToBrNumber(registro.arrecadado), alignment: 'right', fontSize: 7 },
                { text: this.funcaoService.convertToBrNumber(+registro.atualizado - +registro.arrecadado), alignment: 'right', fontSize: 7 }
              ]);
            }
          }
        }
      }
      totalizador.push({
        nome: item.grupo,
        col2: item.totalizadores['previsao'],
        col3: item.totalizadores['atualizado'],
        col5: item.totalizadores['arrecadado'],
        col6: +item.totalizadores['atualizado'] - +item.totalizadores['arrecadado'],
      });
    }

    // Monta os totalizadores do relatorio
    let sub2 = 0;
    let sub3 = 0;
    let sub5 = 0;
    let sub6 = 0;
    let op2 = 0;
    let op3 = 0;
    let op5 = 0;
    let op6 = 0;

    registros.push([{
      text: 'SUBTOTAL DAS RECEITAS ( I )', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub6),
      alignment: 'right', bold: true, fontSize: 7
    }]);


    for (const grupo of grupos) {
      if (grupo.grupo === 'REFINANCIAMENTO ( II )') {
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              grupo.totalizadores[`previsao`]), alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              grupo.totalizadores[`atualizado`]), alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              grupo.totalizadores[`arrecadado`]), alignment: 'right', bold: true, fontSize: 7
          },
          {
            text: this.funcaoService.convertToBrNumber(
              +grupo.totalizadores[`atualizado`] - +grupo.totalizadores[`arrecadado`]), alignment: 'right', bold: true, fontSize: 7
          }
        ]);
        for (const registro of grupo.registros) {
            registros.push([
              {
                text: `\t${registro.nome}`, fontSize: 7
              },
              { text: this.funcaoService.convertToBrNumber(registro.previsao), alignment: 'right', fontSize: 7 },
              { text: this.funcaoService.convertToBrNumber(registro.atualizado), alignment: 'right', fontSize: 7 },
              { text: this.funcaoService.convertToBrNumber(registro.arrecadado), alignment: 'right', fontSize: 7 },
              { text: this.funcaoService.convertToBrNumber(+registro.atualizado - +registro.arrecadado), alignment: 'right', fontSize: 7 }
            ]);
        }
      }
    }

    registros.push([{
      text: 'SUBTOTAL COM REFINANCIAMENTO ( III ) = (I + II)', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub2 + op2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub3 + op3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub5 + op5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub6 + op6),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    registros.push([{
      text: 'DÉFICIT ( IV )', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: '',
      colSpan: 2
    }, {
      text: ''
    }, {
      text: totalLiquidado > (sub5 + op5) ? this.funcaoService.convertToBrNumber(totalLiquidado - (+sub5 + +op5)) : '',
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: ''
    }]);

    registros.push([{
      text: 'TOTAL ( V ) = ( III + IV )', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub2 + op2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub3 + op3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: totalLiquidado > (sub5 + op5) ?
        this.funcaoService.convertToBrNumber((totalLiquidado - (sub5 + op5)) +
          (sub5 + op5)) : this.funcaoService.convertToBrNumber(sub5 + op5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub6 + op6),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    return [{
      layout: 'linhas',
      table: {
        // headers are automatically repeated if the table spans over multiple pages
        // you can declare how many rows should be treated as headers
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 'auto', 'auto', 'auto', 'auto'],
        body: registros
      }
    }];
  }

  private conteudoDespesa(dados: any[], dadosReceita: any[]): {}[] {
    // loop para pegar o total arrecadado e verificar se houve defict ou superavit
    const gruposReceita = this.funcaoService.agrupar(dadosReceita, 'tipo',
      ['previsao', 'atualizado', 'previsto_total', 'arrecadado']);
    let totalArrecadado = 0;
    for (const item of gruposReceita) {
      totalArrecadado = item.grupo !== '(-) DEDUÇÕES DA RECEITA' ?
        totalArrecadado + item.totalizadores['arrecadado'] :
        totalArrecadado - item.totalizadores['arrecadado'];
    }
    // monta o cabecalho
    const registros: {}[] = [
      [
        {
          text: ' ',
          colSpan: 7,
          border: [false, false, false, false]
        }, '', '', '', '', '', ''
      ],
      [{
        text: 'DESPESAS',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }, {
        text: 'DOTAÇÃO INICIAL',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }, {
        text: 'DOTAÇÃO ATUALIZADA',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }, {
        text: 'DESPESAS EMPENHADAS',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }, {
        text: 'DESPESAS LIQUIDADAS',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }, {
        text: 'DESPESAS PAGAS',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }, {
        text: 'SALDO DA DOTAÇÃO',
        alignment: 'center',
        fontSize: 7,
        bold: true,
      }]
    ];

    const grupos = this.funcaoService.agrupar(dados, 'tipo',
      ['dotacao', 'adicionais', 'empenhado', 'liquidado', 'pago']);

    const totalizador = new Array<any>();
    for (const grupo of grupos) {

      // desconsidera esses grupos porque serão impressos somente nos totalizadores
      if (grupo.grupo !== 'AMORTIZAÇÃO DA DÍVIDA - REFINANCIAMENTOS ( IX )' && grupo.grupo !== 'RESERVA DE CONTINGÊNCIA'
        && grupo.grupo !== 'DESPESAS INTRAORÇAMENTÁRIAS') {
        registros.push([
          {
            text: grupo.grupo, bold: true, fontSize: 7
          },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`dotacao`]), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`adicionais`]), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`empenhado`]), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`liquidado`]), alignment: 'right', bold: true, fontSize: 7 },
          { text: this.funcaoService.convertToBrNumber(grupo.totalizadores[`pago`]), alignment: 'right', bold: true, fontSize: 7 },
          {
            text: this.funcaoService.convertToBrNumber(
              (+grupo.totalizadores[`dotacao`] + +grupo.totalizadores[`adicionais`]) - grupo.totalizadores[`empenhado`]),
            alignment: 'right',
            bold: true, fontSize: 7
          }
        ]);

        // lista dos registros do grupo
        for (const registro of grupo.registros) {
          registros.push([
            {
              text: `${registro.nome}`, fontSize: 7
            },
            { text: this.funcaoService.convertToBrNumber(registro.dotacao), alignment: 'right', fontSize: 7 },
            { text: this.funcaoService.convertToBrNumber(registro.adicionais), alignment: 'right', fontSize: 7 },
            { text: this.funcaoService.convertToBrNumber(registro.empenhado), alignment: 'right', fontSize: 7 },
            { text: this.funcaoService.convertToBrNumber(registro.liquidado), alignment: 'right', fontSize: 7 },
            { text: this.funcaoService.convertToBrNumber(registro.pago), alignment: 'right', fontSize: 7 },
            {
              text: this.funcaoService.convertToBrNumber((
                +registro.dotacao + +registro.adicionais) - +registro.empenhado), alignment: 'right', fontSize: 7
            }
          ]);
        }
      }
      totalizador.push({
        nome: grupo.grupo,
        col2: grupo.totalizadores['dotacao'],
        col3: grupo.totalizadores['adicionais'],
        col4: +grupo.totalizadores[`dotacao`] + +grupo.totalizadores[`adicionais`],
        col5: grupo.totalizadores['empenhado'],
        col6: grupo.totalizadores['liquidado'],
        col7: grupo.totalizadores['pago'],
        col8: (+grupo.totalizadores[`dotacao`] + +grupo.totalizadores[`adicionais`]) - +grupo.totalizadores[`empenhado`],
        col9: grupo.totalizadores[`empenhado`] - +grupo.totalizadores[`liquidado`],
        col10: grupo.totalizadores[`liquidado`] - +grupo.totalizadores[`pago`],
      });
    }

    let sub2 = 0; let sub3 = 0; let sub4 = 0; let sub5 = 0; let sub6 = 0; let sub7 = 0; let sub8 = 0; let sub9 = 0; let sub10 = 0;
    let op2 = 0; let op3 = 0; let op4 = 0; let op5 = 0; let op6 = 0; let op7 = 0; let op8 = 0; let op9 = 0; let op10 = 0;
    let intra2 = 0; let intra3 = 0; let intra4 = 0; let intra5 = 0;
    let intra6 = 0; let intra7 = 0; let intra8 = 0; let intra9 = 0; let intra10 = 0;
    let res2 = 0;
    let res3 = 0;
    let res4 = 0;
    for (const val of totalizador) {
      if (val.nome === 'AMORTIZAÇÃO DA DÍVIDA - REFINANCIAMENTOS ( IX )') {
        op2 = op2 + val.col2;
        op3 = op3 + val.col3;
        op4 = op4 + val.col4;
        op5 = op5 + val.col5;
        op6 = op6 + val.col6;
        op7 = op7 + val.col7;
        op8 = op8 + val.col8;
        op9 = op9 + val.col9;
        op10 = op10 + val.col10;
      } else if (val.nome === 'DESPESAS INTRAORÇAMENTÁRIAS') {
        intra2 = intra2 + val.col2;
        intra3 = intra3 + val.col3;
        intra4 = intra4 + val.col4;
        intra5 = intra5 + val.col5;
        intra6 = intra6 + val.col6;
        intra7 = intra7 + val.col7;
        intra8 = intra8 + val.col8;
        intra9 = intra9 + val.col9;
        intra10 = intra10 + val.col10;
      } else if (val.nome === 'RESERVA DE CONTINGÊNCIA') {
        res2 = res2 + val.col2;
        res3 = res3 + val.col3;
        res4 = res4 + val.col4;
      } else {
        sub2 = sub2 + val.col2;
        sub3 = sub3 + val.col3;
        sub4 = sub4 + val.col4;
        sub5 = sub5 + val.col5;
        sub6 = sub6 + val.col6;
        sub7 = sub7 + val.col7;
        sub8 = sub8 + val.col8;
        sub9 = sub9 + val.col9;
        sub10 = sub10 + val.col10;
      }
    }
    // totais da reserva de contigencia
    registros.push([{
      text: 'RESERVA DE CONTINGÊNCIA', bold: true, fontSize: 7,
      alignment: 'left',
    }, {
      text: this.funcaoService.convertToBrNumber(res2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(res3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(res4),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: '',
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: '',
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: '',
      alignment: 'right', bold: true, fontSize: 7
    }]);

    // totais da despesa intra orcamentaria
    registros.push([{
      text: 'DESPESAS INTRAORÇAMENTÁRIAS', bold: true, fontSize: 7,
      alignment: 'left',
    }, {
      text: this.funcaoService.convertToBrNumber(intra2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(intra3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(intra5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(intra6),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(intra7),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(intra8),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    registros.push([{
      text: 'SUBTOTAL DAS DESPESAS ( VIII )', bold: true, fontSize: 7,
      alignment: 'left',
    }, {
      text: this.funcaoService.convertToBrNumber(sub2 + res2 + intra2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub3 + res3 + intra3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub5 + intra5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub6 + intra6),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub7 + intra7),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub8 + intra8),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    registros.push([{
      text: 'AMORTIZAÇÃO DA DÍVIDA - REFINANCIAMENTOS ( IX )', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(op2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(op3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(op5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(op6),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(op7),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(op8),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    registros.push([{
      text: 'SUBTOTAL COM REFINANCIAMENTO (X) = (VIII + IX)', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub2 + op2 + res2 + intra2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub3 + op3 + res3 + intra3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub5 + op5 + intra5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub6 + op6 + intra6),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub7 + op7 + intra7),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub8 + op8 + intra8),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    registros.push([{
      text: 'SUPERÁVIT ( XI )', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: ''
    }, {
      text: ''
    }, {
      text: ''
    }, {
      text: ''
    }, {
      text: totalArrecadado > (sub6 + op6) ? this.funcaoService.convertToBrNumber(totalArrecadado - (sub6 + op6)) : '',
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: '',
    }]);

    registros.push([{
      text: 'TOTAL ( XII ) = ( X + XI )', bold: true,
      alignment: 'left', fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub2 + op2 + res2),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub3 + op3 + res3),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub5 + op5),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: totalArrecadado > (sub6 + op6) ? this.funcaoService.convertToBrNumber(
        (totalArrecadado - (sub6 + op6)) + (sub6 + op6)) : this.funcaoService.convertToBrNumber(sub6 + op6),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub7 + op7),
      alignment: 'right', bold: true, fontSize: 7
    }, {
      text: this.funcaoService.convertToBrNumber(sub8 + op8),
      alignment: 'right', bold: true, fontSize: 7
    }]);

    return [{
      layout: 'linhas',
      table: {
        // headers are automatically repeated if the table spans over multiple pages
        // you can declare how many rows should be treated as headers
        dontBreakRows: true,
        headerRows: 2,
        widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
        body: registros
      }
    }];
  }

}
